/**
 * <nia-header>
 * Panel de navegación móvil deslizante y burbuja de conteo del carrito.
 * El botón de carrito abre el cart drawer con el enlace declarativo
 * on:click="#cart-drawer/toggle" de Horizon, así que aquí no se maneja.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

class NiaHeader extends HTMLElement {
  connectedCallback() {
    this.burger = this.querySelector('.nia-header__burger');
    this.panel = this.querySelector('.nia-header__panel');
    this.overlay = this.querySelector('.nia-header__overlay');
    this.bubble = this.querySelector('.nia-header__bubble');
    this.cartSrText = this.querySelector('.nia-header__cart-sr');

    this.burger?.addEventListener('click', this.#toggle);
    this.overlay?.addEventListener('click', this.#close);
    this.querySelector('.nia-header__panel-close')?.addEventListener('click', this.#close);
    this.panel?.addEventListener('click', (event) => {
      if (event.target instanceof HTMLAnchorElement) this.#close();
    });
    this.querySelector('[data-nia-cart-open]')?.addEventListener('click', this.#openCart);

    // Solo en overlay: al pasar el hero el header necesita fondo sólido.
    if (this.classList.contains('nia-header--over')) {
      this.#watchScroll();
    }
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('shopify:cart:lines-update', this.#onCartUpdate);
    // Lo emite <nia-cart-drawer> al cambiar cantidades desde el propio drawer.
    document.addEventListener('nia:cart:updated', this.#onDrawerUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('shopify:cart:lines-update', this.#onCartUpdate);
    document.removeEventListener('nia:cart:updated', this.#onDrawerUpdate);
    this.observer?.disconnect();
    this.sentinel?.remove();
    document.body.style.overflow = '';
  }

  /**
   * Un centinela de 80px al inicio de la página marca el umbral. Con
   * IntersectionObserver no hay listener de scroll en el hilo principal.
   */
  #watchScroll() {
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:80px;pointer-events:none;';
    document.body.prepend(sentinel);

    this.observer = new IntersectionObserver(
      ([entry]) => this.classList.toggle('is-scrolled', !entry.isIntersecting),
      { threshold: 0 }
    );
    this.observer.observe(sentinel);
    this.sentinel = sentinel;
  }

  #openCart = (event) => {
    const drawer = document.getElementById('nia-cart-drawer');
    if (!drawer || typeof drawer.toggle !== 'function') return;
    event.preventDefault();
    drawer.toggle();
  };

  #onDrawerUpdate = (event) => {
    this.#setCount(event.detail?.itemCount ?? 0);
  };

  /** @param {number} count */
  #setCount(count) {
    if (this.bubble) {
      this.bubble.textContent = count > 99 ? '99+' : String(count);
      this.bubble.hidden = count === 0;
    }
    if (this.cartSrText) {
      this.cartSrText.textContent = `${count} artículos en el carrito`;
    }
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  #toggle = () => {
    this.isOpen ? this.#close() : this.#open();
  };

  #open = () => {
    this.classList.add('is-open');
    this.burger?.setAttribute('aria-expanded', 'true');
    this.panel?.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    this.panel?.querySelector(FOCUSABLE)?.focus();
  };

  #close = () => {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.burger?.setAttribute('aria-expanded', 'false');
    this.panel?.setAttribute('inert', '');
    document.body.style.overflow = '';
    this.burger?.focus();
  };

  /** Esc cierra; Tab queda atrapado dentro del panel mientras está abierto. */
  #onKeydown = (event) => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
      return;
    }

    if (event.key !== 'Tab' || !this.panel) return;

    const focusable = Array.from(this.panel.querySelectorAll(FOCUSABLE));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        this.#setCount(cart?.totalQuantity ?? detail?.itemCount ?? 0);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[nia-header] Evento de carrito rechazado:', error);
      });
  };
}

if (!customElements.get('nia-header')) {
  customElements.define('nia-header', NiaHeader);
}
